import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Settings, User, Lock, Bell, Eye, Shield, Trash2, Download, 
  Link2, Church, Mail, Phone, HelpCircle, MessageSquare, 
  ChevronRight, Loader2, Check, AlertTriangle, LogOut, BellRing, BellOff, Ban,
  Star, Heart, ShoppingBag, Calendar
} from "lucide-react";
import { 
  getMe, updateProfile, changePassword, deleteAccount, 
  getUserSettings, updateUserSettings, leaveParish, clearAuthData,
  createSupportTicket, getPushStatus, updateNotificationPreferences,
  getBlockedUsers, unblockUser
} from "@/lib/api";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "sonner";

export default function SettingsPage({ user: initialUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(initialUser);
  const [activeSection, setActiveSection] = useState("contact");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Push notifications hook
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePushHook
  } = usePushNotifications();
  
  // Contact Details
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [name, setName] = useState(user?.name || "");
  
  // Password Change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Notification Preferences - granular per-type
  const [notifications, setNotifications] = useState({
    reviews: { in_app: true, email: true, sms: false },
    mentions: { in_app: true, email: true, sms: false },
    comments: { in_app: true, email: true, sms: false },
    likes: { in_app: true, email: false, sms: false },
    messages: { in_app: true, email: true, sms: false },
    friend_requests: { in_app: true, email: true, sms: false },
    marketplace: { in_app: true, email: true, sms: false },
    parish_announcements: { in_app: true, email: true, sms: false },
    event_reminders: { in_app: true, email: true, sms: true },
  });
  
  // Privacy Settings
  const [privacy, setPrivacy] = useState({
    profile_visibility: "everyone", // everyone, parish, friends
    who_can_message: "everyone",
    listings_visibility: "everyone"
  });
  
  // Blocked Users
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [unblocking, setUnblocking] = useState(null);
  
  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveParishDialogOpen, setLeaveParishDialogOpen] = useState(false);
  const [parishToLeave, setParishToLeave] = useState(null);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportCategory, setSupportCategory] = useState("general");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  
  // Support categories definition
  const supportCategories = [
    { id: "general", label: "General Question" },
    { id: "bug", label: "Report a Bug" },
    { id: "feature", label: "Feature Request" },
    { id: "account", label: "Account Issue" },
    { id: "parish_request", label: "Request to Add a Parish" },
    { id: "other", label: "Other" }
  ];
  
  useEffect(() => {
    loadSettings();
  }, []);
  
  // Handle query parameters for support section
  useEffect(() => {
    const section = searchParams.get("section");
    const category = searchParams.get("category");
    
    if (section === "support") {
      setActiveSection("support");
      if (category) {
        setSupportCategory(category);
        setSupportDialogOpen(true);
        // Clear query params after using them
        setSearchParams({});
      }
    }
  }, [searchParams]);
  
  // Load blocked users when privacy section is active
  useEffect(() => {
    if (activeSection === "privacy") {
      loadBlockedUsers();
    }
  }, [activeSection]);
  
  const loadSettings = async () => {
    try {
      const res = await getUserSettings();
      if (res.data.notifications) {
        // Merge loaded settings with defaults
        setNotifications(prev => {
          const loaded = res.data.notifications;
          const merged = { ...prev };
          const types = ['reviews', 'mentions', 'comments', 'likes', 'messages', 'friend_requests', 'marketplace', 'parish_announcements', 'event_reminders'];
          types.forEach(type => {
            if (loaded[type]) merged[type] = { ...prev[type], ...loaded[type] };
          });
          return merged;
        });
      }
      if (res.data.privacy) setPrivacy(res.data.privacy);
    } catch (err) {
      // Settings might not exist yet, use defaults
    }
  };
  
  const loadBlockedUsers = async () => {
    setLoadingBlocked(true);
    try {
      const res = await getBlockedUsers();
      setBlockedUsers(res.data.blocked_users || []);
    } catch (err) {
      // Silently fail
    }
    setLoadingBlocked(false);
  };
  
  const handleUnblockUser = async (userId, userName) => {
    setUnblocking(userId);
    try {
      await unblockUser(userId);
      setBlockedUsers(blockedUsers.filter(u => u.user_id !== userId));
      toast.success(`Unblocked ${userName}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unblock user");
    }
    setUnblocking(null);
  };
  
  const handleSaveContact = async () => {
    setSaving(true);
    try {
      await updateProfile({ name, email, phone });
      toast.success("Contact details updated");
      // Refresh user data
      const res = await getMe();
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };
  
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setSaving(false);
  };
  
  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await updateUserSettings({ notifications });
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error("Failed to save preferences");
    }
    setSaving(false);
  };
  
  const handleSavePrivacy = async () => {
    setSaving(true);
    try {
      await updateUserSettings({ privacy });
      toast.success("Privacy settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };
  
  const handleLeaveParish = async () => {
    if (!parishToLeave) return;
    setSaving(true);
    try {
      await leaveParish(parishToLeave.parish_id);
      toast.success(`You have left ${parishToLeave.name}`);
      setLeaveParishDialogOpen(false);
      setParishToLeave(null);
      // Refresh user data
      const res = await getMe();
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to leave parish");
    }
    setSaving(false);
  };
  
  const openLeaveParishDialog = (parish) => {
    setParishToLeave(parish);
    setLeaveParishDialogOpen(true);
  };
  
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    setSaving(true);
    try {
      await deleteAccount();
      toast.success("Account deleted");
      clearAuthData();
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete account");
    }
    setSaving(false);
  };
  
  const handleContactSupport = async () => {
    if (!supportMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    setSaving(true);
    try {
      const categoryLabel = supportCategories.find(c => c.id === supportCategory)?.label || "Support Request";
      await createSupportTicket({
        subject: categoryLabel,
        message: supportMessage.trim(),
        category: supportCategory
      });
      toast.success("Support request sent! We'll get back to you soon.");
      setSupportMessage("");
      setSupportCategory("general");
      setSupportDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send support request");
    }
    setSaving(false);
  };
  
  const handleDownloadData = async () => {
    toast.info("Preparing your data download...");
    // In a real app, this would trigger a data export
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success("Your data export has been emailed to you.");
    } catch (err) {
      toast.error("Failed to export data");
    }
  };

  const sections = [
    { id: "contact", label: "Contact Details", icon: User },
    { id: "password", label: "Change Password", icon: Lock },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "privacy", label: "Privacy", icon: Eye },
    { id: "parish", label: "Parish Settings", icon: Church },
    { id: "account", label: "Account", icon: Shield },
    { id: "support", label: "Support", icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="settings-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[var(--liturgical-primary)] rounded-lg">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-['Crimson_Text'] font-bold text-[#2D2A26]">Settings</h1>
            <p className="text-sm text-[#6B665E] font-['Nunito']">Manage your account and preferences</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar Navigation */}
          <div className="md:w-64 flex-shrink-0">
            <Card className="bg-white border-[#E5E0D5] sticky top-24">
              <CardContent className="p-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors font-['Nunito'] text-sm ${
                      activeSection === section.id
                        ? 'bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)]'
                        : 'text-[#6B665E] hover:bg-[#F9F7F2]'
                    }`}
                    data-testid={`settings-nav-${section.id}`}
                  >
                    <section.icon className="w-4 h-4" />
                    {section.label}
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-6">
            {/* Contact Details */}
            {activeSection === "contact" && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader>
                  <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
                    <User className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Contact Details
                  </CardTitle>
                  <CardDescription className="font-['Nunito']">
                    Update your personal information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] text-sm">Full Name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="border-[#E5E0D5] font-['Nunito']"
                      data-testid="settings-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] text-sm">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-4 h-4 text-[#6B665E]" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 border-[#E5E0D5] font-['Nunito']"
                        data-testid="settings-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] text-sm">Phone Number (optional)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 w-4 h-4 text-[#6B665E]" />
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="pl-10 border-[#E5E0D5] font-['Nunito']"
                        data-testid="settings-phone-input"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={handleSaveContact}
                    disabled={saving}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Change Password */}
            {activeSection === "password" && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader>
                  <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Change Password
                  </CardTitle>
                  <CardDescription className="font-['Nunito']">
                    Update your account password
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {user?.has_password === false ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800 font-['Nunito']">
                        You signed up with Google. To set a password, use the "Forgot Password" option on the login page.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label className="font-['Nunito'] text-sm">Current Password</Label>
                        <Input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="border-[#E5E0D5] font-['Nunito']"
                          data-testid="settings-current-password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-['Nunito'] text-sm">New Password</Label>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Min 6 characters"
                          className="border-[#E5E0D5] font-['Nunito']"
                          data-testid="settings-new-password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-['Nunito'] text-sm">Confirm New Password</Label>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="border-[#E5E0D5] font-['Nunito']"
                          data-testid="settings-confirm-password"
                        />
                      </div>
                      <Button 
                        onClick={handleChangePassword}
                        disabled={saving || !currentPassword || !newPassword}
                        className="bg-[var(--liturgical-primary)] hover:brightness-90"
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                        Change Password
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notification Preferences */}
            {activeSection === "notifications" && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader>
                  <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
                    <Bell className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Notification Preferences
                  </CardTitle>
                  <CardDescription className="font-['Nunito']">
                    Choose how you want to be notified for each activity type
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {/* Header row */}
                  <div className="grid grid-cols-[1fr_60px_60px_60px] gap-2 items-center px-3 py-2 bg-[#F9F7F2] rounded-lg mb-1">
                    <span className="font-['Nunito'] text-xs font-semibold text-[#6B665E] uppercase tracking-wide">Activity</span>
                    <span className="font-['Nunito'] text-xs font-semibold text-[#6B665E] text-center" title="In-app notification bell"><BellRing className="w-3.5 h-3.5 mx-auto" /></span>
                    <span className="font-['Nunito'] text-xs font-semibold text-[#6B665E] text-center" title="Email notifications"><Mail className="w-3.5 h-3.5 mx-auto" /></span>
                    <span className="font-['Nunito'] text-xs font-semibold text-[#6B665E] text-center" title="SMS notifications"><Phone className="w-3.5 h-3.5 mx-auto" /></span>
                  </div>

                  {/* Notification rows */}
                  {[
                    { key: "reviews", label: "Reviews on your listings", icon: Star },
                    { key: "mentions", label: "@Mentions", icon: User },
                    { key: "comments", label: "Comments on your posts", icon: MessageSquare },
                    { key: "likes", label: "Likes on your posts", icon: Heart },
                    { key: "messages", label: "New messages", icon: MessageSquare },
                    { key: "friend_requests", label: "Friend requests", icon: User },
                    { key: "marketplace", label: "Marketplace activity", icon: ShoppingBag },
                    { key: "parish_announcements", label: "Parish announcements", icon: Church },
                    { key: "event_reminders", label: "Event reminders", icon: Calendar },
                  ].map(({ key, label, icon: Icon }) => (
                    <div
                      key={key}
                      className="grid grid-cols-[1fr_60px_60px_60px] gap-2 items-center px-3 py-2.5 border-b border-[#F0EBE0] last:border-b-0 hover:bg-[#F9F7F2]/50 transition-colors rounded"
                      data-testid={`notification-row-${key}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Icon className="w-4 h-4 text-[var(--liturgical-primary)] flex-shrink-0" />
                        <span className="font-['Nunito'] text-sm text-[#2D2A26] truncate">{label}</span>
                      </div>
                      <div className="flex justify-center">
                        <Switch
                          checked={notifications[key]?.in_app ?? true}
                          onCheckedChange={(checked) => setNotifications(prev => ({
                            ...prev, [key]: { ...prev[key], in_app: checked }
                          }))}
                          data-testid={`notif-${key}-inapp`}
                        />
                      </div>
                      <div className="flex justify-center">
                        <Switch
                          checked={notifications[key]?.email ?? true}
                          onCheckedChange={(checked) => setNotifications(prev => ({
                            ...prev, [key]: { ...prev[key], email: checked }
                          }))}
                          data-testid={`notif-${key}-email`}
                        />
                      </div>
                      <div className="flex justify-center">
                        <Switch
                          checked={notifications[key]?.sms ?? false}
                          onCheckedChange={(checked) => setNotifications(prev => ({
                            ...prev, [key]: { ...prev[key], sms: checked }
                          }))}
                          data-testid={`notif-${key}-sms`}
                        />
                      </div>
                    </div>
                  ))}

                  {!user?.phone && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs text-amber-800 font-['Nunito']">
                        <strong>SMS notifications:</strong> Add your phone number in Contact Details to enable SMS notifications.
                      </p>
                    </div>
                  )}

                  <Separator className="my-4" />
                  
                  {/* Push Notifications Device Toggle */}
                  <div className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg">
                    <div>
                      <Label className="font-['Nunito'] font-semibold text-sm">Push Notifications (this device)</Label>
                      <p className="text-xs text-[#6B665E] font-['Nunito']">
                        {!pushSupported 
                          ? "Not supported in this browser" 
                          : pushSubscribed 
                            ? "Enabled on this device" 
                            : "Enable browser push notifications"}
                      </p>
                    </div>
                    {pushSupported ? (
                      pushSubscribed ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const success = await unsubscribePushHook();
                            if (success) toast.success("Push notifications disabled");
                          }}
                          disabled={pushLoading}
                          className="border-[#E5E0D5]"
                        >
                          {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellOff className="w-4 h-4 mr-1" />}
                          Disable
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={async () => {
                            const success = await subscribePush();
                            if (success) {
                              toast.success("Push notifications enabled!");
                            } else if (pushPermission === 'denied') {
                              toast.error("Please enable notifications in your browser settings");
                            }
                          }}
                          disabled={pushLoading}
                          className="bg-[var(--liturgical-primary)] hover:brightness-90"
                        >
                          {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4 mr-1" />}
                          Enable
                        </Button>
                      )
                    ) : (
                      <Badge variant="secondary" className="font-['Nunito']">Not Supported</Badge>
                    )}
                  </div>
                  
                  <Button 
                    onClick={handleSaveNotifications}
                    disabled={saving}
                    className="w-full mt-4 bg-[var(--liturgical-primary)] hover:brightness-90"
                    data-testid="save-notification-prefs"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Save Preferences
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Privacy Settings */}
            {activeSection === "privacy" && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader>
                  <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Privacy Settings
                  </CardTitle>
                  <CardDescription className="font-['Nunito']">
                    Control who can see your information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] font-semibold">Profile Visibility</Label>
                    <p className="text-xs text-[#6B665E]">Who can see your profile</p>
                    <Select 
                      value={privacy.profile_visibility} 
                      onValueChange={(value) => setPrivacy({...privacy, profile_visibility: value})}
                    >
                      <SelectTrigger className="border-[#E5E0D5]" data-testid="settings-profile-visibility">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="parish">Parish members only</SelectItem>
                        <SelectItem value="friends">Friends only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] font-semibold">Who Can Message Me</Label>
                    <p className="text-xs text-[#6B665E]">Control who can send you direct messages</p>
                    <Select 
                      value={privacy.who_can_message} 
                      onValueChange={(value) => setPrivacy({...privacy, who_can_message: value})}
                    >
                      <SelectTrigger className="border-[#E5E0D5]" data-testid="settings-messaging">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="parish">Parish members only</SelectItem>
                        <SelectItem value="friends">Friends only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-['Nunito'] font-semibold">Marketplace Listings Visibility</Label>
                    <p className="text-xs text-[#6B665E]">Who can see your marketplace listings</p>
                    <Select 
                      value={privacy.listings_visibility} 
                      onValueChange={(value) => setPrivacy({...privacy, listings_visibility: value})}
                    >
                      <SelectTrigger className="border-[#E5E0D5]" data-testid="settings-listings-visibility">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="parish">Parish members only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    onClick={handleSavePrivacy}
                    disabled={saving}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Save Privacy Settings
                  </Button>
                  
                  {/* Blocked Users Section */}
                  <Separator className="my-6" />
                  <div className="space-y-4">
                    <div>
                      <Label className="font-['Nunito'] font-semibold flex items-center gap-2">
                        <Ban className="w-4 h-4 text-[#991B1B]" />
                        Blocked Users
                      </Label>
                      <p className="text-xs text-[#6B665E] mt-1">Users you've blocked won't be able to see your content and you won't see theirs</p>
                    </div>
                    
                    {loadingBlocked ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-[#6B665E]" />
                      </div>
                    ) : blockedUsers.length === 0 ? (
                      <div className="bg-[#F9F7F2] rounded-lg p-4 text-center">
                        <p className="text-sm text-[#6B665E] font-['Nunito']">You haven't blocked anyone</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {blockedUsers.map((blockedUser) => (
                          <div 
                            key={blockedUser.user_id}
                            className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]"
                            data-testid={`blocked-user-${blockedUser.user_id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-[#E5E0D5] overflow-hidden flex items-center justify-center">
                                {blockedUser.picture ? (
                                  <img src={blockedUser.picture} alt={blockedUser.name} className="w-full h-full object-cover" />
                                ) : (
                                  <User className="w-5 h-5 text-[#6B665E]" />
                                )}
                              </div>
                              <div>
                                <p className="font-['Nunito'] font-semibold text-sm text-[#2D2A26]">{blockedUser.name}</p>
                                <p className="text-xs text-[#6B665E]">
                                  Blocked {blockedUser.blocked_at ? new Date(blockedUser.blocked_at).toLocaleDateString() : 'recently'}
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnblockUser(blockedUser.user_id, blockedUser.name)}
                              disabled={unblocking === blockedUser.user_id}
                              className="border-[#E5E0D5] text-[#6B665E] hover:text-[#2D2A26] font-['Nunito'] text-xs"
                              data-testid={`unblock-user-${blockedUser.user_id}`}
                            >
                              {unblocking === blockedUser.user_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                "Unblock"
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Parish Settings */}
            {activeSection === "parish" && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader>
                  <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
                    <Church className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Parish Settings
                  </CardTitle>
                  <CardDescription className="font-['Nunito']">
                    Manage your parish memberships
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {user?.parishes && user.parishes.length > 0 ? (
                    <div className="space-y-3">
                      {user.parishes.map((parish) => (
                        <div key={parish.parish_id} className="bg-[#F9F7F2] rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-['Nunito'] font-semibold text-[#2D2A26]">{parish.name}</p>
                              <p className="text-xs text-[#6B665E]">
                                {parish.is_admin ? "Parish Admin" : "Member"}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {parish.is_admin ? (
                                <Badge className="bg-purple-100 text-purple-800 border-0">Admin</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800 border-0">Member</Badge>
                              )}
                              <Button 
                                variant="ghost"
                                size="sm"
                                onClick={() => openLeaveParishDialog(parish)}
                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                              >
                                <LogOut className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Church className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                      <p className="text-[#6B665E] font-['Nunito'] mb-4">You're not a member of any parish</p>
                      <Button 
                        onClick={() => navigate("/parishes")}
                        className="bg-[var(--liturgical-primary)] hover:brightness-90"
                      >
                        Find a Parish
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Account Management */}
            {activeSection === "account" && (
              <div className="space-y-6">
                {/* Linked Accounts */}
                <Card className="bg-white border-[#E5E0D5]">
                  <CardHeader>
                    <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
                      <Link2 className="w-5 h-5 text-[var(--liturgical-primary)]" />
                      Linked Accounts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border">
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                        </div>
                        <div>
                          <p className="font-['Nunito'] font-semibold">Google</p>
                          <p className="text-xs text-[#6B665E]">
                            {user?.picture ? "Connected" : "Not connected"}
                          </p>
                        </div>
                      </div>
                      <Badge className={user?.picture ? "bg-green-100 text-green-800 border-0" : "bg-gray-100 text-gray-600 border-0"}>
                        {user?.picture ? "Linked" : "Not Linked"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Download Data */}
                <Card className="bg-white border-[#E5E0D5]">
                  <CardHeader>
                    <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
                      <Download className="w-5 h-5 text-[var(--liturgical-primary)]" />
                      Download My Data
                    </CardTitle>
                    <CardDescription className="font-['Nunito']">
                      Get a copy of all your Parish data
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
                      Download a copy of your posts, messages, and profile information.
                    </p>
                    <Button 
                      variant="outline"
                      onClick={handleDownloadData}
                      className="border-[#E5E0D5]"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Request Data Export
                    </Button>
                  </CardContent>
                </Card>

                {/* Delete Account */}
                <Card className="bg-white border-red-200">
                  <CardHeader>
                    <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2 text-red-600">
                      <Trash2 className="w-5 h-5" />
                      Delete Account
                    </CardTitle>
                    <CardDescription className="font-['Nunito']">
                      Permanently delete your account and all data
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-800 font-['Nunito']">
                          <p className="font-semibold mb-1">This action cannot be undone</p>
                          <p>All your posts, messages, listings, and profile information will be permanently deleted.</p>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="destructive"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete My Account
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Support */}
            {activeSection === "support" && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader>
                  <CardTitle className="font-['Crimson_Text'] text-xl flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Support
                  </CardTitle>
                  <CardDescription className="font-['Nunito']">
                    Get help or report an issue
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button 
                    variant="outline"
                    onClick={() => setSupportDialogOpen(true)}
                    className="w-full justify-start border-[#E5E0D5] h-auto py-4"
                  >
                    <MessageSquare className="w-5 h-5 mr-3 text-[var(--liturgical-primary)]" />
                    <div className="text-left">
                      <p className="font-['Nunito'] font-semibold">Contact Support</p>
                      <p className="text-xs text-[#6B665E]">Send us a message and we'll get back to you</p>
                    </div>
                    <ChevronRight className="w-5 h-5 ml-auto text-[#6B665E]" />
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/community-guidelines")}
                    className="w-full justify-start border-[#E5E0D5] h-auto py-4"
                  >
                    <Shield className="w-5 h-5 mr-3 text-[var(--liturgical-primary)]" />
                    <div className="text-left">
                      <p className="font-['Nunito'] font-semibold">Community Guidelines</p>
                      <p className="text-xs text-[#6B665E]">Learn about our community standards</p>
                    </div>
                    <ChevronRight className="w-5 h-5 ml-auto text-[#6B665E]" />
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/privacy")}
                    className="w-full justify-start border-[#E5E0D5] h-auto py-4"
                  >
                    <Eye className="w-5 h-5 mr-3 text-[var(--liturgical-primary)]" />
                    <div className="text-left">
                      <p className="font-['Nunito'] font-semibold">Privacy Policy</p>
                      <p className="text-xs text-[#6B665E]">How we handle your data</p>
                    </div>
                    <ChevronRight className="w-5 h-5 ml-auto text-[#6B665E]" />
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/terms")}
                    className="w-full justify-start border-[#E5E0D5] h-auto py-4"
                  >
                    <Shield className="w-5 h-5 mr-3 text-[var(--liturgical-primary)]" />
                    <div className="text-left">
                      <p className="font-['Nunito'] font-semibold">Terms of Service</p>
                      <p className="text-xs text-[#6B665E]">Our terms and conditions</p>
                    </div>
                    <ChevronRight className="w-5 h-5 ml-auto text-[#6B665E]" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Leave Parish Dialog */}
      <Dialog open={leaveParishDialogOpen} onOpenChange={(open) => {
        setLeaveParishDialogOpen(open);
        if (!open) setParishToLeave(null);
      }}>
        <DialogContent className="bg-white border-[#E5E0D5]">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl">Leave Parish?</DialogTitle>
            <DialogDescription className="font-['Nunito']">
              Are you sure you want to leave <strong>{parishToLeave?.name}</strong>? You'll need to request to rejoin if you change your mind.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveParishDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleLeaveParish}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Leave Parish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-[#E5E0D5]">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-red-600">Delete Account</DialogTitle>
            <DialogDescription className="font-['Nunito']">
              This will permanently delete your account and all associated data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-[#6B665E] font-['Nunito']">
              Type <strong>DELETE</strong> to confirm:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="border-[#E5E0D5]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={saving || deleteConfirmText !== "DELETE"}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Support Dialog */}
      <Dialog open={supportDialogOpen} onOpenChange={(open) => {
        setSupportDialogOpen(open);
        if (!open) {
          setSupportCategory("general");
          setSupportMessage("");
        }
      }}>
        <DialogContent className="bg-white border-[#E5E0D5]">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl">Contact Support</DialogTitle>
            <DialogDescription className="font-['Nunito']">
              Describe your issue and we'll get back to you as soon as possible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-['Nunito'] text-[#2D2A26]">Category</Label>
              <Select value={supportCategory} onValueChange={setSupportCategory}>
                <SelectTrigger className="border-[#E5E0D5]">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {supportCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-['Nunito'] text-[#2D2A26]">Message</Label>
              <Textarea
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder={supportCategory === "parish_request" 
                  ? "Please provide the parish name, city, state/country, and any additional details..."
                  : "Describe your issue or question..."}
                className="border-[#E5E0D5] min-h-[120px] font-['Nunito']"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupportDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleContactSupport}
              disabled={saving || !supportMessage.trim()}
              className="bg-[var(--liturgical-primary)] hover:brightness-90"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
